import React, { FC, useState, useEffect } from 'react';
import { Modal } from 'antd';
import { useParams } from 'react-router-dom';
import { Button } from 'src/components/Button/Button';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/close-icon.svg';
import { useAddClientNote } from 'src/api/mutations/useClientNotesMutations';
import './AddNoteModal.scss';

interface AddNoteModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddNoteModal: FC<AddNoteModalProps> = ({ open, onClose }) => {
  const { uid } = useParams();
  const { mutate: addNote, isPending } = useAddClientNote();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (open) {
      setTitle('');
      setContent('');
      setTitleError('');
      setContentError('');
      setServerError('');
    }
  }, [open]);

  const validate = () => {
    let isValid = true;
    setTitleError('');
    setContentError('');

    if (!title.trim()) {
      setTitleError('Title is required');
      isValid = false;
    } else if (!/[a-zA-Z]/.test(title)) {
      setTitleError('Title must contain at least one valid letter');
      isValid = false;
    } else if (title.length > 100) {
      setTitleError('Title cannot exceed 100 characters');
      isValid = false;
    }

    if (!content.trim()) {
      setContentError('Note content is required');
      isValid = false;
    } else if (!/[a-zA-Z]/.test(content)) {
      setContentError('Note content must contain at least one valid letter');
      isValid = false;
    }

    return isValid;
  };

  const handleSave = () => {
    if (!uid) return;
    if (validate()) {
      setServerError('');
      addNote(
        { clientId: uid, title: title.trim(), description: content.trim() },
        {
          onSuccess: () => {
            onClose();
          },
          onError: (error: any) => {
            // Standardized API error code mapping
            if (error.message.includes('ERR_INVALID_TITLE')) {
              setTitleError('Title must contain valid characters');
            } else if (error.message.includes('ERR_INVALID_DESCRIPTION')) {
              setContentError('Description must contain valid characters');
            } else {
              setServerError(error.message || 'Failed to save note. Please try again.');
            }
          },
        },
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={540}
      closeIcon={null}
      rootClassName="add-note-modal assign-client-modal"
      className="logout-modal"
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(6, 7, 35, 0.8)',
        },
      }}
    >
      <div className="assign-client-content add-note-content">
        <div className="modal-header">
          <h2 className="modal-title">Add Note</h2>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="form-group title-group" style={{ position: 'relative' }}>
          <label>NOTE TITLE</label>
          <div className="input-wrapper" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Enter a short title"
              maxLength={100}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (titleError) setTitleError('');
              }}
              onBlur={(e) => setTitle(e.target.value.trim())}
              style={{
                width: '100%',
                paddingRight: '50px',
                borderColor: titleError ? 'red' : undefined,
              }}
            />
          </div>
          {titleError && (
            <span
              className="error-message"
              style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}
            >
              {titleError}
            </span>
          )}
        </div>

        <div className="form-group note-group">
          <label>
            NOTE <span className="char-count">{content.length}/500</span>
          </label>
          <div className="textarea-wrapper" style={contentError ? { borderColor: 'red' } : {}}>
            <textarea
              placeholder="Write your note here..."
              maxLength={500}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                if (contentError) setContentError('');
              }}
              onBlur={(e) => setContent(e.target.value.trim())}
            />
          </div>
          {contentError && (
            <span
              className="error-message"
              style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}
            >
              {contentError}
            </span>
          )}
        </div>
        {serverError && (
          <div
            className="server-error"
            style={{ color: 'red', fontSize: '12px', marginBottom: '16px' }}
          >
            {serverError}
          </div>
        )}

        <div className="modal-actions">
          <Button className="btn-cancel" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="btn-assign-primary btn-save"
            onClick={handleSave}
            disabled={isPending || (!title.trim() && !content.trim())}
            isLoading={isPending}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};
